/* eslint-disable @typescript-eslint/member-ordering */

import { computed, inject, Injectable, REQUEST, Signal, signal, WritableSignal } from '@angular/core';
import { CookieService } from '../cookie';

const CONTENT_LANGUAGE_COOKIE_KEY: string = 'cms-content-language';
const CONTENT_LANGUAGE_COOKIE_MAX_AGE_SECONDS: number = 60 * 60 * 24 * 365;
const CONTENT_PATH_LANGUAGE_PATTERN: RegExp = /^\/([a-z]{2})(?:[/?#]|$)/;

/**
 * The CMS owns the content language.
 *
 * A request carries a standard `Accept-Language` resolved as
 * `URL prefix -> cms-content-language cookie -> original client header`,
 *  and the `Content-Language` of the response becomes the active language.
 */
@Injectable({ providedIn: 'root' })
export class CmsLanguageService {
  private readonly cookieService: CookieService = inject(CookieService);
  private readonly request: Request | null = inject(REQUEST, { optional: true });
  private readonly contentLanguage: WritableSignal<string | undefined> = signal<string | undefined>(this.getCookieLanguage());
  private readonly requestPathLanguage: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
  private requestPath: string | undefined;

  /** Uses the requested URL language until the CMS reports the language it actually served. */
  public readonly language: Signal<string | undefined> = computed(
    (): string | undefined => this.requestPathLanguage() ?? this.contentLanguage(),
  );

  public getRequestLanguage(contentPath?: string): string | undefined {
    return this.getPathLanguage(contentPath)
      ?? this.requestPathLanguage()
      ?? this.contentLanguage()
      ?? this.request?.headers.get('accept-language')
      ?? undefined;
  }

  /**
   * Stores the current path for matching CMS responses and makes its URL language
   * available to requests without persisting it as a served language.
   */
  public setRequestPath(contentPath: string): void {
    this.requestPath = contentPath;
    this.requestPathLanguage.set(this.getPathLanguage(contentPath));
  }

  public getRequestPath(): string | undefined {
    return this.requestPath;
  }

  /** Adopts the `Content-Language` the CMS reported for a page. */
  public storeResponseLanguage(contentLanguage: string | null | undefined): void {
    if (!contentLanguage) {
      return;
    }

    this.cookieService.set(CONTENT_LANGUAGE_COOKIE_KEY, contentLanguage, {
      maxAgeSeconds: CONTENT_LANGUAGE_COOKIE_MAX_AGE_SECONDS,
    });
    this.contentLanguage.set(contentLanguage);
    this.requestPathLanguage.set(undefined);
  }

  private getCookieLanguage(): string | undefined {
    return this.cookieService.get(CONTENT_LANGUAGE_COOKIE_KEY);
  }

  /**
   * A language prefix in the URL takes precedence over the stored language.
   * Any two-letter prefix is forwarded unchanged. The CMS determines whether
   * content exists and returns the effective language via `Content-Language`.
   */
  private getPathLanguage(contentPath?: string): string | undefined {
    return CONTENT_PATH_LANGUAGE_PATTERN.exec(contentPath ?? '')?.[1];
  }
}
